import { useConsoleReadState } from "@/contexts/ConsoleReadState";
import type { SetupInputConnection } from "@/api/setup-input";
import { usePaginatedQuery } from "@/hooks/usePaginatedQuery";
import { apiClient, ApiError, apiErrorMessage } from "@/api/client";
import type { AppBindingResponse, AppBindingSummary } from "@/types/api";
import { useCallback, useEffect, useRef, useState } from "react";
import type { KintoneConnectionAccess } from "@/api/kintone-environment";
import {
  disableSecureAccess,
  fetchSecureAccessSetting,
  replaceSecureAccessCertificate,
  type DisableSecureAccessRequest,
  type ReplaceSecureAccessCertificateRequest,
  type SecureAccessSetting,
  type SecureAccessMutationResult,
} from "@/api/kintone-environment";
import type { publicOperatorAdmin } from "@kintone-storage/openapi-types";
import type { EditableFieldBinding, AppBindingModeId } from "../app-binding-ux";

type FieldSettingImportResponse =
  publicOperatorAdmin.components["schemas"]["FieldSettingImportResponse"];
export type PluginInstallationResponse =
  publicOperatorAdmin.components["schemas"]["PluginInstallationResponse"];
export type PluginInstallationState = PluginInstallationResponse["state"];
export type AppBindingHealthCheckResponse =
  publicOperatorAdmin.components["schemas"]["AppBindingHealthCheckResponse"];
type ReflectionResponse =
  publicOperatorAdmin.components["schemas"]["ReflectionResponse"];
type KintoneAppProbeFieldsResponse =
  publicOperatorAdmin.components["schemas"]["KintoneAppProbeFieldsResponse"];
type PaginatedQueryOptions = Pick<NonNullable<Parameters<typeof usePaginatedQuery>[2]>, "cursor" | "onCursorChange">;
export type AttachmentFileState =
  publicOperatorAdmin.components["schemas"]["AttachmentFileState"];
export type AttachmentStorageAlignmentPlanPreviewRequest =
  publicOperatorAdmin.components["schemas"]["AttachmentStorageAlignmentPlanPreviewRequest"];
export type AttachmentStorageAlignmentPlanPreviewResponse =
  publicOperatorAdmin.components["schemas"]["AttachmentStorageAlignmentPlanPreviewResponse"];
export type AttachmentStorageAlignmentExecutionStartRequest =
  publicOperatorAdmin.components["schemas"]["AttachmentStorageAlignmentExecutionStartRequest"];
export type AttachmentStorageAlignmentExecutionOptions =
  publicOperatorAdmin.components["schemas"]["AttachmentStorageAlignmentExecutionOptions"];
export type AttachmentStorageAlignmentExecutionMode =
  publicOperatorAdmin.components["schemas"]["AttachmentStorageAlignmentExecutionMode"];
export type AttachmentStorageAlignmentFieldPlan =
  publicOperatorAdmin.components["schemas"]["AttachmentStorageAlignmentFieldPlan"];
export type OrphanRecordFolderCleanupPlanPreviewRequest =
  publicOperatorAdmin.components["schemas"]["OrphanRecordFolderCleanupPlanPreviewRequest"];
export type OrphanRecordFolderCleanupPlanPreviewResponse =
  publicOperatorAdmin.components["schemas"]["OrphanRecordFolderCleanupPlanPreviewResponse"];
export type OrphanRecordFolderCleanupExecutionStartRequest =
  publicOperatorAdmin.components["schemas"]["OrphanRecordFolderCleanupExecutionStartRequest"];
export type AsynchronousExecutionState =
  publicOperatorAdmin.components["schemas"]["AsynchronousExecutionState"];
export type AsynchronousExecutionKind =
  publicOperatorAdmin.components["schemas"]["CustomerVisibleAsynchronousExecutionKind"];
export type AsynchronousExecutionResponse =
  publicOperatorAdmin.components["schemas"]["AsynchronousExecutionResponse"];
export type AsynchronousExecutionDetailResponse =
  publicOperatorAdmin.components["schemas"]["AsynchronousExecutionDetailResponse"];
export type AsynchronousExecutionCancelRequest =
  publicOperatorAdmin.components["schemas"]["AsynchronousExecutionCancelRequest"];
export type AsynchronousExecutionResumeRequest =
  publicOperatorAdmin.components["schemas"]["AsynchronousExecutionResumeRequest"];

function isApiErrorLike(error: unknown): error is ApiError {
  return (
    error instanceof ApiError ||
    (error instanceof Error &&
      typeof (error as { code?: unknown }).code === "string" &&
      typeof (error as { status?: unknown }).status === "number")
  );
}

function messageForProbeFieldsError(error: ApiError): string {
  const messages: Record<string, string> = {
    kintone_api_token_unauthorized:
      "入力した kintone API トークンが拒否されました。対象アプリ用のトークンであること、発行または変更後に kintone で「アプリを更新」していることを確認してください。",
    kintone_api_token_forbidden:
      "入力した kintone API トークンにアプリ情報またはフィールド設定を読む権限がありません。レコード閲覧またはレコード追加の権限を付与し、発行または変更後に kintone で「アプリを更新」してください。",
    kintone_app_metadata_unavailable:
      "kintoneアプリ情報を取得できませんでした。API トークン発行または変更後に kintone で「アプリを更新」していない、またはアプリが未公開の可能性があります。「アプリを更新」してから再試行してください。",
    kintone_app_not_found:
      "指定した kintoneアプリが見つかりません。アプリ URL、アプリ ID、公開状態を確認してください。",
    kintone_field_probe_unavailable:
      "kintone フィールド設定を取得できませんでした。API トークンの権限と、発行または変更後に kintone で「アプリを更新」していることを確認してください。",
  };
  return messages[error.code] ?? error.message;
}

export function attachmentManagementAdmissionErrorMessage(error: unknown): string | null {
  if (!isApiErrorLike(error)) return null;
  const message = error.code === "asynchronous_execution_automatic_alignment_active"
    ? "自動整合化が有効、または停止処理中のため開始できません。最新状態を確認し、自動整合化を停止して、開始済みジョブの収束後に開始してください。"
    : error.code === "asynchronous_execution_request_conflict"
      ? "同じアプリで進行中または要対応の実行があります。完了を待つか、要対応を解消して「再開」してください。新しくやり直す場合は、表示中の実行を「取消」してから開始してください。"
      : null;
  return message && error.requestId ? `${message}（requestId: ${error.requestId}）` : message;
}

export function attachmentStorageAlignmentStartErrorMessage(error: unknown): string {
  return attachmentManagementAdmissionErrorMessage(error) ?? attachmentStorageAlignmentAuthorityErrorMessage(
    error,
    "整合化の開始に失敗しました。",
  );
}

export function attachmentStorageAlignmentPreviewErrorMessage(error: unknown): string {
  return attachmentStorageAlignmentAuthorityErrorMessage(
    error,
    "実行内容を確認できませんでした。",
  );
}

function attachmentStorageAlignmentAuthorityErrorMessage(
  error: unknown,
  fallback: string,
): string {
  if (!isApiErrorLike(error)) return apiErrorMessage(error, fallback);
  const messages: Record<string, string> = {
    asynchronous_execution_preview_stale:
      "確認後に設定が変更されました。もう一度［実行内容を確認］を押してください。",
    asynchronous_execution_current_authority_unavailable:
      "現在のアプリ設定、反映済み設定、またはStorageの準備状態を確認できませんでした。設定を確認・反映した後、もう一度［実行内容を確認］を押してください。",
    kintone_api_token_unauthorized:
      "保存済みのkintone APIトークンが拒否されました。対象アプリ用のトークンと、kintoneでの［アプリを更新］を確認してください。",
    kintone_api_token_forbidden:
      "保存済みのkintone APIトークンにフィールド設定を読む権限がありません。権限を付与してkintoneで［アプリを更新］した後、再試行してください。",
    kintone_app_not_found:
      "対象のkintoneアプリが見つかりません。アプリID、ゲストスペース設定、公開状態を確認してください。",
    kintone_form_layout_unavailable:
      "kintoneのフォームレイアウトを取得できませんでした。時間をおいて［実行内容を確認］を押してください。",
    kintone_field_setting_import_unavailable:
      "kintoneのフィールド設定を取得できませんでした。時間をおいて［実行内容を確認］を押してください。",
  };
  const message = messages[error.code];
  if (!message) return apiErrorMessage(error, fallback);
  return error.requestId ? `${message}（requestId: ${error.requestId}）` : message;
}

export function useAppBindingList(filters?: Record<string, string>) {
  return usePaginatedQuery<AppBindingSummary>("/kintone-apps", filters);
}

export function useAppBindingDetail(kintoneAppId: string) {
  const [binding, setBinding] = useState<AppBindingResponse | null>(null);
  const [isLoading, setIsLoading] = useState(Boolean(kintoneAppId));
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    if (!kintoneAppId) {
      setBinding(null);
      setError(null);
      setIsLoading(false);
      return;
    }
    setIsLoading(true);
    try {
      const data = await apiClient<AppBindingResponse>(
        `/kintone-apps/${kintoneAppId}/binding`,
      );
      setBinding(data);
      setError(null);
    } catch (err) {
      const message =
        err instanceof ApiError
          ? err.message
          : err instanceof Error
            ? err.message
            : "Fetch failed";
      setError(message);
      setBinding(null);
    } finally {
      setIsLoading(false);
    }
  }, [kintoneAppId]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  return { binding, isLoading, error, refresh };
}

function migrationBasePath(kintoneAppId: string) {
  return `/kintone-apps/${kintoneAppId}`;
}

export function useAsynchronousExecutions(
  filters?: Partial<Record<"kintoneAppId" | "executionKind" | "executionState" | "activeOnly" | "completedDiagnosticOnly" | "completedManualAlignmentOnly" | "alignmentOrigin" | "limit", string>>,
  options?: PaginatedQueryOptions,
) {
  return usePaginatedQuery<AsynchronousExecutionResponse>(
    "/asynchronous-executions",
    filters,
    options,
  );
}

export function useAsynchronousExecutionDetail(executionId: string, pollWhileActive = false) {
  const [execution, setExecution] = useState<AsynchronousExecutionDetailResponse | null>(null);
  const [isLoading, setIsLoading] = useState(Boolean(executionId));
  const [error, setError] = useState<string | null>(null);
  const [fetchRevision, setFetchRevision] = useState(0);
  const lastExecution = useRef<AsynchronousExecutionDetailResponse | null>(null);
  const requestSequence = useRef(0);
  const inFlightExecutionId = useRef<string | null>(null);
  const failures = useRef(0);
  const retryAllowed = useRef(true);
  const refresh = useCallback(async () => {
    if (!executionId || inFlightExecutionId.current === executionId) return;
    inFlightExecutionId.current = executionId;
    const sequence = ++requestSequence.current;
    setIsLoading(lastExecution.current === null);
    try {
      const result = await apiClient<AsynchronousExecutionDetailResponse>(
        `/asynchronous-executions/${executionId}`,
      );
      if (sequence !== requestSequence.current) return;
      lastExecution.current = result;
      setExecution(result);
      setError(null);
      failures.current = 0;
      retryAllowed.current = true;
    } catch (err) {
      if (sequence !== requestSequence.current) return;
      setError(apiErrorMessage(err));
      failures.current++;
      retryAllowed.current = !(err instanceof ApiError) || [0, 408, 429, 500, 502, 503, 504].includes(err.status);
    } finally {
      if (sequence === requestSequence.current) {
        inFlightExecutionId.current = null;
        setIsLoading(false);
        setFetchRevision((current) => current + 1);
      }
    }
  }, [executionId]);
  useEffect(() => {
    lastExecution.current = null;
    inFlightExecutionId.current = null;
    failures.current = 0;
    retryAllowed.current = true;
    setExecution(null);
    setError(null);
    setIsLoading(Boolean(executionId));
    void refresh();
    return () => { requestSequence.current++; };
  }, [executionId, refresh]);
  useEffect(() => {
    if (!pollWhileActive || !executionId || isLoading || !retryAllowed.current ||
      (execution && ["completed", "failed", "canceled"].includes(execution.executionState))) return;
    const timer = setTimeout(() => void refresh(), Math.min(30_000, 3000 * 2 ** Math.min(failures.current, 4)));
    return () => clearTimeout(timer);
  }, [pollWhileActive, executionId, execution, isLoading, fetchRevision, refresh]);
  return { execution, isLoading, error, refresh };
}

export function useAttachmentStorageAlignmentPlanPreview(
  kintoneAppId: string,
) {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const mutate = useCallback(
    async (
      body: AttachmentStorageAlignmentPlanPreviewRequest,
    ): Promise<AttachmentStorageAlignmentPlanPreviewResponse> => {
      setIsLoading(true);
      setError(null);
      try {
        return await apiClient<AttachmentStorageAlignmentPlanPreviewResponse>(
          `${migrationBasePath(kintoneAppId)}/attachment-storage-alignment-plan-preview`,
          {
            method: "POST",
            body: JSON.stringify(body),
          },
        );
      } catch (err) {
        setError(attachmentStorageAlignmentPreviewErrorMessage(err));
        throw err;
      } finally {
        setIsLoading(false);
      }
    },
    [kintoneAppId],
  );

  const reset = useCallback(() => setError(null), []);
  return { mutate, isLoading, error, reset };
}

export function useStartAttachmentStorageAlignmentExecution(
  kintoneAppId: string,
) {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const mutate = useCallback(
    async (
      body: AttachmentStorageAlignmentExecutionStartRequest,
    ): Promise<AsynchronousExecutionResponse> => {
      setIsLoading(true);
      setError(null);
      try {
        return await apiClient<AsynchronousExecutionResponse>(
          `${migrationBasePath(kintoneAppId)}/attachment-storage-alignment-executions`,
          {
            method: "POST",
            body: JSON.stringify(body),
          },
        );
      } catch (err) {
        setError(attachmentStorageAlignmentStartErrorMessage(err));
        throw err;
      } finally {
        setIsLoading(false);
      }
    },
    [kintoneAppId],
  );

  const reset = useCallback(() => setError(null), []);
  return { mutate, isLoading, error, reset };
}

export function useOrphanRecordFolderCleanupPlanPreview(
  kintoneAppId: string,
) {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const mutate = useCallback(
    async (
      body: OrphanRecordFolderCleanupPlanPreviewRequest = {},
    ): Promise<OrphanRecordFolderCleanupPlanPreviewResponse> => {
      setIsLoading(true);
      setError(null);
      try {
        return await apiClient<OrphanRecordFolderCleanupPlanPreviewResponse>(
          `${migrationBasePath(kintoneAppId)}/orphan-record-folder-cleanup-plan-preview`,
          {
            method: "POST",
            body: JSON.stringify(body),
          },
        );
      } catch (err) {
        setError(apiErrorMessage(err, "Unknown error"));
        throw err;
      } finally {
        setIsLoading(false);
      }
    },
    [kintoneAppId],
  );

  return { mutate, isLoading, error };
}

export function useStartOrphanRecordFolderCleanupExecution(
  kintoneAppId: string,
) {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const mutate = useCallback(
    async (
      body: OrphanRecordFolderCleanupExecutionStartRequest,
    ): Promise<AsynchronousExecutionResponse> => {
      setIsLoading(true);
      setError(null);
      try {
        return await apiClient<AsynchronousExecutionResponse>(
          `${migrationBasePath(kintoneAppId)}/orphan-record-folder-cleanup-executions`,
          {
            method: "POST",
            body: JSON.stringify(body),
          },
        );
      } catch (err) {
        setError(attachmentManagementAdmissionErrorMessage(err) ?? apiErrorMessage(err, "Unknown error"));
        throw err;
      } finally {
        setIsLoading(false);
      }
    },
    [kintoneAppId],
  );

  return { mutate, isLoading, error };
}

export function useAsynchronousExecutionCommand(
  kintoneAppId: string,
  executionKind: AsynchronousExecutionKind,
  executionId: string,
  command: "cancel" | "resume",
) {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const mutate = useCallback(
    async (
      body: AsynchronousExecutionCancelRequest | AsynchronousExecutionResumeRequest,
    ): Promise<AsynchronousExecutionResponse> => {
      setIsLoading(true);
      setError(null);
      try {
        const family = executionKind === "attachment_storage_alignment"
          ? "attachment-storage-alignment"
          : executionKind === "attachment_environment_restore_preview"
            ? "attachment-environment-restore-preview"
          : executionKind === "attachment_environment_restore"
            ? "attachment-environment-restore"
            : executionKind === "attachment_recovery_export"
              ? "attachment-recovery-export"
              : "orphan-record-folder-cleanup";
        return await apiClient<AsynchronousExecutionResponse>(
          `${migrationBasePath(kintoneAppId)}/${family}-executions/${executionId}/${command}`,
          {
            method: "POST",
            body: JSON.stringify(body),
          },
        );
      } catch (err) {
        setError(apiErrorMessage(err, "Unknown error"));
        throw err;
      } finally {
        setIsLoading(false);
      }
    },
    [kintoneAppId, executionKind, executionId, command],
  );

  return { mutate, isLoading, error };
}

export function useUpsertAppBinding() {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [errorCode, setErrorCode] = useState<string | null>(null);

  const mutate = useCallback(
    async (body: {
      kintoneAppId: string;
      guestSpaceId?: string | null;
      storageId: string;
      modeId: AppBindingModeId;
      sharedStorageAccessMode?: "read_only" | "read_write" | null;
      compatibleAttachmentRemovalPolicy?: "archive" | "delete";
      appBaseFolder: string;
      headerLauncherEnabled: boolean;
      automaticAlignmentStartOnSetup?: boolean;
      replaceNativeAttachmentFields: boolean;
      showHiddenStorageEntries: boolean;
      maxUploadFileBytes?: number | null;
      thumbnailSourceProfile: {
        maxSourceBytes: number;
        maxSourcePixels: number;
      };
      recordFolderKind: "record_id" | "record_label_field" | null;
      recordFolderLabelFieldBindingKey?: string | null;
      kintoneApiToken?: string | null;
      targetFieldBindings: EditableFieldBinding[];
      externalizationConfirmed?: boolean;
      pendingAlignmentCancellation?: { executionId: string; revision: string };
    }): Promise<AppBindingResponse> => {
      setIsLoading(true);
      setError(null);
      setErrorCode(null);
      try {
        const requestBody = {
          ...(body.guestSpaceId ? { guestSpaceId: body.guestSpaceId } : {}),
          ...(body.kintoneApiToken ? { kintoneApiToken: body.kintoneApiToken } : {}),
          ...(body.externalizationConfirmed === undefined ? {} : { externalizationConfirmed: body.externalizationConfirmed }),
          ...(body.pendingAlignmentCancellation ? { pendingAlignmentCancellation: body.pendingAlignmentCancellation } : {}),
          storageId: body.storageId,
          modeId: body.modeId,
          sharedStorageAccessMode:
            body.modeId === "shared_storage"
              ? body.sharedStorageAccessMode ?? "read_write"
              : null,
          appBaseFolder: body.appBaseFolder,
          ...(body.compatibleAttachmentRemovalPolicy === undefined ? {} : { compatibleAttachmentRemovalPolicy: body.compatibleAttachmentRemovalPolicy }),
          headerLauncherEnabled: body.headerLauncherEnabled,
          replaceNativeAttachmentFields: body.replaceNativeAttachmentFields,
          showHiddenStorageEntries: body.showHiddenStorageEntries,
          ...(body.maxUploadFileBytes === undefined ? {} : { maxUploadFileBytes: body.maxUploadFileBytes }),
          thumbnailSourceProfile: body.thumbnailSourceProfile,
          recordFolderKind: body.recordFolderKind,
          recordFolderLabelFieldBindingKey:
            body.recordFolderKind === "record_label_field"
              ? body.recordFolderLabelFieldBindingKey ?? null
              : null,
          targetFieldBindings: body.targetFieldBindings.map((binding) => ({
            fieldBindingKey: binding.fieldBindingKey,
            fieldCode: binding.fieldCode.trim(),
            targetScopeKind: binding.targetScopeKind,
            subtableFieldCode:
              binding.targetScopeKind === "subtable_row"
                ? binding.subtableFieldCode.trim()
                : null,
            customAttachmentEnabled: true,
            thumbnailSetting: {
              enabled: binding.thumbnailEnabled,
              sizePx:
                binding.thumbnailEnabled && binding.thumbnailSizePx.trim()
                  ? Number.parseInt(binding.thumbnailSizePx, 10)
                  : null,
              importedFromKintoneAt: binding.importedFromKintoneAt,
              importSourceState: binding.importSourceState,
            },
          })),
        };
        return await apiClient<AppBindingResponse>(
          `/kintone-apps/${body.kintoneAppId}/binding`,
          {
            method: "PUT",
            body: JSON.stringify(requestBody),
          },
        );
      } catch (err) {
        const message =
          err instanceof ApiError
            ? err.message
            : err instanceof Error
              ? err.message
              : "Unknown error";
        setError(message);
        setErrorCode(err instanceof ApiError ? err.code : null);
        throw err;
      } finally {
        setIsLoading(false);
      }
    },
    [],
  );

  const reset = useCallback(() => { setError(null); setErrorCode(null); }, []);

  return { mutate, isLoading, error, errorCode, reset };
}

export function useRequestReflection(kintoneAppId: string) {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const mutate = useCallback(async (): Promise<ReflectionResponse> => {
    setIsLoading(true);
    setError(null);
    try {
      return await apiClient<ReflectionResponse>(
        `/kintone-apps/${kintoneAppId}/reflection-requests`,
        {
          method: "POST",
          body: JSON.stringify({
            pendingReason: "plugin_reapply_required",
            recoveryOwner: "kintone_admin",
          }),
        },
      );
    } catch (err) {
      const message =
        err instanceof ApiError
          ? err.message
          : err instanceof Error
            ? err.message
            : "Unknown error";
      setError(message);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, [kintoneAppId]);

  const reset = useCallback(() => setError(null), []);

  return {
    mutate,
    isLoading,
    error,
    reset,
  };
}

export function useDisconnectPluginLinkage(kintoneAppId: string) {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const mutate = useCallback(async (): Promise<void> => {
    setIsLoading(true);
    setError(null);
    try {
      await apiClient<void>(
        `/kintone-apps/${kintoneAppId}/plugin-linkage`,
        { method: "DELETE" },
      );
    } catch (err) {
      const message =
        err instanceof ApiError
          ? err.message
          : err instanceof Error
            ? err.message
            : "Unknown error";
      setError(message);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, [kintoneAppId]);

  const reset = useCallback(() => setError(null), []);

  return {
    mutate,
    isLoading,
    error,
    reset,
  };
}

export function useDeleteAppBinding(kintoneAppId: string) {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setError(null);
  }, [kintoneAppId]);

  const mutate = useCallback(async (options: { force?: boolean } = {}): Promise<void> => {
    setIsLoading(true);
    setError(null);
    try {
      const appBindingDeleteAction = options.force
        ? "confirmed_setting_only_delete"
        : "safe_setting_delete";
      await apiClient<void>(
        `/kintone-apps/${kintoneAppId}/binding?appBindingDeleteAction=${appBindingDeleteAction}`,
        { method: "DELETE" },
      );
    } catch (err) {
      const message =
        err instanceof ApiError
          ? err.message
          : err instanceof Error
            ? err.message
            : "Unknown error";
      setError(message);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, [kintoneAppId]);

  const reset = useCallback(() => setError(null), []);

  return {
    mutate,
    isLoading,
    error,
    reset,
  };
}

export function useImportFieldSettings(kintoneAppId: string) {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const mutate = useCallback(
    async (
      targetFieldBindings?: Array<{
        fieldBindingKey: string;
        fieldCode: string;
        targetScopeKind: "root_field" | "subtable_row";
        subtableFieldCode: string | null;
      }>,
    ) => {
      setIsLoading(true);
      setError(null);
      try {
        return await apiClient<FieldSettingImportResponse>(
          `/kintone-apps/${kintoneAppId}/field-setting-imports`,
          {
            method: "POST",
            body: JSON.stringify({
              importMode: "thumbnail_defaults",
              ...(targetFieldBindings && targetFieldBindings.length > 0
                ? { targetFieldBindings }
                : {}),
            }),
          },
        );
      } catch (err) {
        const message =
          err instanceof ApiError
            ? err.message
            : err instanceof Error
              ? err.message
              : "Unknown error";
        setError(message);
        throw err;
      } finally {
        setIsLoading(false);
      }
    },
    [kintoneAppId],
  );

  return { mutate, isLoading, error };
}

export function useEnsurePluginInstallation(
  kintoneAppId: string,
) {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const mutate = useCallback(
    async (confirm = false): Promise<PluginInstallationResponse> => {
    setIsLoading(true);
    setError(null);
    try {
      return await apiClient<PluginInstallationResponse>(
        `/kintone-apps/${kintoneAppId}/plugin-installation`,
        { method: "POST", body: JSON.stringify({ confirm }) },
      );
    } catch (err) {
      const message =
        err instanceof ApiError
          ? err.message
          : err instanceof Error
            ? err.message
            : "Unknown error";
      setError(message);
      throw err;
    } finally {
      setIsLoading(false);
    }
    },
    [kintoneAppId],
  );

  return { mutate, isLoading, error };
}

export type SecureAccessView = SecureAccessSetting;

/**
 * Read-only detail-open health check (DES-SETUP-020): token疎通 → plugin presence
 * → version. Returns the structured result; on error keeps the error code so the
 * banner can distinguish e.g. `kintone_api_token_missing`.
 */
export function useHealthCheck(kintoneAppId: string) {
  const [result, setResult] = useState<AppBindingHealthCheckResponse | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<{ code: string; message: string } | null>(null);

  const run = useCallback(async (): Promise<AppBindingHealthCheckResponse | null> => {
    if (!kintoneAppId) return null;
    setIsLoading(true);
    setError(null);
    try {
      const r = await apiClient<AppBindingHealthCheckResponse>(
        `/kintone-apps/${kintoneAppId}/health-check`,
        { method: "POST" },
      );
      setResult(r);
      return r;
    } catch (err) {
      const code = err instanceof ApiError ? err.code : "health_check_failed";
      const message =
        err instanceof ApiError
          ? err.message
          : err instanceof Error
            ? err.message
            : "ヘルスチェックに失敗しました。";
      setError({ code, message });
      setResult(null);
      return null;
    } finally {
      setIsLoading(false);
    }
  }, [kintoneAppId]);

  return { run, result, isLoading, error };
}

export interface KintoneCatalogAppItem {
  appId: string;
  name: string;
  /** Already bound in this organization → a new setup is not applicable. */
  bound: boolean;
}

export interface KintoneCatalogSpaceItem {
  kind: "normal" | "guest";
  spaceId: string;
  name: string | null;
  apps: KintoneCatalogAppItem[];
}

export interface KintoneAppCatalogResponse {
  /** Candidate or registered subdomain validated by the console BFF. */
  kintoneSubdomain: string;
  /** Present only when this connection request committed an explicit save directive. */
  savedCredential: KintoneAdminCredentialSummary | null;
  home: KintoneCatalogAppItem[];
  spaces: KintoneCatalogSpaceItem[];
}

export type EasySetupCredentialSource =
  | {
      kind: "one_time_input";
      username: string;
      password: string;
      mfa?: { kind: "disabled" } | { kind: "totp_key"; setupKey: string } | { kind: "totp_runtime" };
      saveDirective?: "none" | "save_shared" | "save_personal";
      expectedCredentialRevision?: number;
      overwriteConfirmed?: boolean;
    }
  | {
      kind: "saved_shared" | "saved_personal";
      expectedCredentialRevision?: number;
    };

export interface KintoneAdminCredentialSummary {
  mfaMode: "disabled" | "totp_key" | "totp_runtime";
  credentialScopeKind: "shared" | "personal";
  kintoneSubdomain: string;
  kintoneAdminAccountName: string;
  credentialRevision: number;
  credentialState: "verified" | "verification_required" | "disabled";
  lastFailureReasonCode: string | null;
  lastVerifiedAt: string | null;
  lastUsedAt: string | null;
  updatedByCustomerUserId: string | null;
  updatedAt: string;
}

export interface KintoneAdminCredentialSummaries {
  shared: KintoneAdminCredentialSummary | null;
  personal: KintoneAdminCredentialSummary | null;
}

/**
 * Easy-setup app picker source. Posts the administrator credentials once and
 * returns the apps grouped by home / regular space / guest space. The selected
 * credential source may be one-time input or a saved shared/personal credential.
 */
export function useKintoneAppCatalog() {
  const [catalog, setCatalog] = useState<KintoneAppCatalogResponse | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<{ code: string; message: string } | null>(null);

  const fetchCatalog = useCallback(
    async (input: {
      targetSubdomain: string;
      connectionAccess?: KintoneConnectionAccess;
      credentialSource: EasySetupCredentialSource;
      inputConnection?: SetupInputConnection;
    }): Promise<KintoneAppCatalogResponse | null> => {
      setIsLoading(true);
      setError(null);
      try {
        const { inputConnection, ...body } = input;
        const r = inputConnection
          ? await inputConnection.request<KintoneAppCatalogResponse>("catalog", body)
          : await apiClient<KintoneAppCatalogResponse>("/console-bff/kintone-apps", { method: "POST", body: JSON.stringify(body) });
        const requestedSavedScope =
          input.credentialSource.kind === "one_time_input" &&
          input.credentialSource.saveDirective === "save_shared"
            ? "shared"
            : input.credentialSource.kind === "one_time_input" &&
                input.credentialSource.saveDirective === "save_personal"
              ? "personal"
              : null;
        const savedCredentialIsValid =
          r.savedCredential !== null &&
          r.savedCredential.kintoneSubdomain === r.kintoneSubdomain &&
          Number.isInteger(r.savedCredential.credentialRevision) &&
          r.savedCredential.credentialRevision > 0;
        if (requestedSavedScope &&
            (!savedCredentialIsValid ||
             r.savedCredential?.credentialScopeKind !== requestedSavedScope ||
             r.savedCredential?.credentialState !== "verified")) {
          throw new Error(
            "kintoneへの接続には成功しましたが、管理者アカウントの保存結果を確認できませんでした。",
          );
        }
        if (!requestedSavedScope && r.savedCredential !== null) {
          throw new Error(
            "要求していない管理者アカウント保存結果が返されました。もう一度やり直してください。",
          );
        }
        setCatalog(r);
        return r;
      } catch (err) {
        const code = err instanceof ApiError ? err.code : "kintone_apps_list_failed";
        const message =
          err instanceof ApiError
            ? err.message
            : err instanceof Error
              ? err.message
              : "アプリ一覧の取得に失敗しました。";
        setError({ code, message });
        setCatalog(null);
        return null;
      } finally {
        setIsLoading(false);
      }
    },
    [],
  );

  const reset = useCallback(() => {
    setCatalog(null);
    setError(null);
  }, []);

  return { catalog, fetchCatalog, isLoading, error, reset };
}

export function useKintoneAdminCredentials(options: { enabled?: boolean } = {}) {
  const enabled = options.enabled ?? true;
  const [credentials, setCredentials] =
    useState<KintoneAdminCredentialSummaries | null>(null);
  const [isLoading, setIsLoading] = useState(enabled);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async (options?: { force?: boolean }) => {
    if (!enabled && !options?.force) {
      setCredentials(null);
      setError(null);
      setIsLoading(false);
      return;
    }
    setIsLoading(true);
    try {
      const data = await apiClient<KintoneAdminCredentialSummaries>(
        "/console-bff/kintone-admin-credentials",
      );
      setCredentials(data);
      setError(null);
    } catch (err) {
      setCredentials(null);
      setError(apiErrorMessage(err, "kintone管理者アカウントの取得に失敗しました。"));
    } finally {
      setIsLoading(false);
    }
  }, [enabled]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  return { credentials, isLoading, error, refresh };
}

export function useSecureAccessSettings(options: { enabled?: boolean } = {}) {
  const enabled = options.enabled ?? true;
  const state = useConsoleReadState();
  const resource = state.resource<SecureAccessView>("secure-access");
  const view = enabled ? resource.value : null;
  const isLoading = enabled && resource.loading;
  const error = enabled ? resource.error : null;

  const refresh = useCallback(async () => {
    if (enabled) await state.readResource("secure-access", fetchSecureAccessSetting);
  }, [enabled, state]);

  useEffect(() => { void refresh(); }, [refresh]);

  const replaceCertificate = useCallback(async (
    input: ReplaceSecureAccessCertificateRequest,
    idempotencyKey: string,
  ): Promise<SecureAccessMutationResult> => {
    const result = await replaceSecureAccessCertificate(input, idempotencyKey);
    if ("connectionKind" in result) state.publishResource("secure-access", result);
    return result;
  }, [state]);

  const disable = useCallback(async (
    input: DisableSecureAccessRequest,
    idempotencyKey: string,
  ): Promise<SecureAccessMutationResult> => {
    const result = await disableSecureAccess(input, idempotencyKey);
    if ("connectionKind" in result) state.publishResource("secure-access", result);
    return result;
  }, [state]);

  return { view, isLoading, error, refresh, replaceCertificate, disable };
}

export interface ConsentStatus {
  consentKind: string;
  requiredVersion: number;
  consented: boolean;
  agreedVersion: number | null;
  agreedAt: string | null;
}

export function useConsent(consentKind: string) {
  const [status, setStatus] = useState<ConsentStatus | null>(null);
  const [isLoading, setIsLoading] = useState(Boolean(consentKind));
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    if (!consentKind) return;
    setIsLoading(true);
    try {
      const data = await apiClient<ConsentStatus>(
        `/console-bff/consents/${consentKind}`,
      );
      setStatus(data);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Fetch failed");
    } finally {
      setIsLoading(false);
    }
  }, [consentKind]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const agree = useCallback(async (presentedVersion: number): Promise<ConsentStatus> => {
    const data = await apiClient<ConsentStatus>(
      `/console-bff/consents/${consentKind}`,
      { method: "POST", body: JSON.stringify({ presentedVersion }) },
    );
    setStatus(data);
    return data;
  }, [consentKind]);

  return { status, isLoading, error, refresh, agree };
}

export function useFeatures() {
  const [headlessSetupEnabled, setHeadlessSetupEnabled] = useState(false);
  useEffect(() => {
    void apiClient<{ headlessSetupEnabled?: boolean }>("/console-bff/features")
      .then((d) => setHeadlessSetupEnabled(Boolean(d.headlessSetupEnabled)))
      .catch(() => setHeadlessSetupEnabled(false));
  }, []);
  return { headlessSetupEnabled };
}

export interface SetupJobStepView {
  id: string;
  state: "pending" | "running" | "done" | "failed" | "skipped";
  reason?: string;
}

export interface SetupJobView {
  jobId: string;
  kintoneSubdomain: string;
  kintoneAppId: string;
  status: "queued" | "running" | "cancel_requested" | "succeeded" | "failed" | "fallback_manual" | "canceled" | "reconciliation_required";
  steps: SetupJobStepView[];
  reason?: string;
  createdAt: string;
  updatedAt: string;
}

const TERMINAL_JOB_STATUSES = ["succeeded", "failed", "fallback_manual", "canceled", "reconciliation_required"];

export function useSetupJob() {
  const [job, setJob] = useState<SetupJobView | null>(null);
  const [issuedToken, setIssuedToken] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  /** API error code of a refused job start (e.g. app_limit_reached), so the flow can show guidance. */
  const [errorCode, setErrorCode] = useState<string | null>(null);
  const timer = useRef<ReturnType<typeof setInterval> | null>(null);

  const requestId = useRef<string | null>(null);
  const pollFailures = useRef(0);
  const pollBusy = useRef(false);
  const nextPollAt = useRef(0);
  const discoveryGeneration = useRef(0);

  const stopPolling = useCallback(() => {
    if (timer.current) {
      clearInterval(timer.current);
      timer.current = null;
    }
  }, []);

  useEffect(() => () => stopPolling(), [stopPolling]);

  const poll = useCallback(
    async (jobId: string, expectedAppId?: string) => {
      if (pollBusy.current || Date.now() < nextPollAt.current) return;
      pollBusy.current = true;
      try {
        const view = await apiClient<SetupJobView>(
          `/console-bff/setup-jobs/${jobId}`,
        );
        if (view.jobId !== jobId || (expectedAppId !== undefined && view.kintoneAppId !== expectedAppId)) {
          throw new Error("setup_job_response_scope_mismatch");
        }
        setJob(view);
        pollFailures.current = 0;
        nextPollAt.current = 0;
        setError(null);
        if (TERMINAL_JOB_STATUSES.includes(view.status)) {
          if (view.status !== "reconciliation_required") requestId.current = null;
          stopPolling();
          // succeeded（単一 deploy 成功＝activated）でのみ発行済みトークンを consume し、
          // Phase2 のフィールド取得へ引き継ぐ。deploy 前失敗時の staged トークンはサーバ側
          // の再実行（同メモ再利用）用で、ここでは consume しない（DES-SETUP-008/010）。
          if (view.status === "succeeded") {
            try {
              const token = await apiClient<{ apiToken: string }>(
                `/console-bff/setup-jobs/${jobId}/issued-token`,
                { method: "POST" },
              );
              setIssuedToken(token.apiToken);
            } catch (err) {
              setError(apiErrorMessage(
                err,
                "セットアップは完了しましたが、発行済みトークンの取得に失敗しました。",
              ));
            }
          }
        }
        return view;
      } catch (err) {
        pollFailures.current++;
        nextPollAt.current = Date.now() + Math.min(30_000, 1500 * 2 ** pollFailures.current);
        if (!(err instanceof ApiError && err.status === 503) || pollFailures.current >= 10) stopPolling();
        setError("進捗を更新できませんでした。時間をおいて画面を開き直すと確認できます。開始操作は繰り返さないでください。");
      } finally {
        pollBusy.current = false;
      }
    },
    [stopPolling],
  );

  useEffect(() => {
    let disposed = false;
    const generation = ++discoveryGeneration.current;
    void apiClient<{ job: SetupJobView | null }>("/console-bff/setup-jobs/current").then(({ job: active }) => {
      if (disposed || generation !== discoveryGeneration.current || !active) return;
      setJob(active);
      if (!TERMINAL_JOB_STATUSES.includes(active.status)) timer.current = setInterval(() => void poll(active.jobId), 1500);
    }).catch(() => { if (!disposed) setError("進行中のセットアップを確認できませんでした。"); });
    return () => { disposed = true; stopPolling(); };
  }, [poll, stopPolling]);

  const start = useCallback(
    async (input: {
      kintoneAppId: string;
      guestSpaceId?: string | null;
      credentialSource: EasySetupCredentialSource;
      modeId: AppBindingModeId;
      storageId?: string;
      appBaseFolder?: string;
      kintoneAppName?: string;
      /** "repair" re-does broken steps on an activated/completed binding. */
      mode?: "new" | "repair";
      inputConnection?: SetupInputConnection;
    }) => {
      stopPolling();
      discoveryGeneration.current++;
      nextPollAt.current = 0;
      pollFailures.current = 0;
      setError(null);
      setErrorCode(null);
      setIssuedToken(null);
      setJob(null);
      try {
        const { inputConnection, ...body } = input;
        const request = { ...body, requestId: requestId.current ??= crypto.randomUUID() };
        const view = inputConnection
          ? await inputConnection.request<SetupJobView>("start_job", request)
          : await apiClient<SetupJobView>("/console-bff/setup-jobs", { method: "POST", body: JSON.stringify(request) });
        setJob(view);
        timer.current = setInterval(() => void poll(view.jobId), 1500);
      } catch (err) {
        // A lost POST response does not mean the admitted job was not started.
        // Recover only this request's receipt; never resend credentials or POST.
        if (err instanceof ApiError && (err.status === 0 || err.status >= 500) && requestId.current) {
          const recovered = await poll(requestId.current, input.kintoneAppId);
          if (recovered) {
            if (!TERMINAL_JOB_STATUSES.includes(recovered.status)) timer.current = setInterval(() => void poll(recovered.jobId), 1500);
            return;
          }
        }
        if (err instanceof ApiError && err.status >= 400 && err.status < 500 && err.status !== 409) requestId.current = null;
        // ジョブ作成（初期 app binding 生成＋連携トークン mint）の失敗は jobError に出し、
        // 呼び出し側（EasySetupFlow）が入力画面へ戻せるよう再 throw する。
        const message =
          err instanceof ApiError
            ? err.message
            : err instanceof Error
              ? err.message
              : "セットアップの開始に失敗しました。";
        setError(message);
        setErrorCode(err instanceof ApiError ? err.code : null);
        throw err;
      }
    },
    [poll, stopPolling],
  );

  const cancel = useCallback(async () => {
    if (!job) return;
    try {
      const view = await apiClient<SetupJobView>(`/console-bff/setup-jobs/${job.jobId}`, {
        method: "DELETE",
      });
      setJob(view);
    } catch (err) {
      setError(apiErrorMessage(err, "セットアップジョブのキャンセルに失敗しました。"));
    }
  }, [job, stopPolling]);

  const refresh = useCallback(async () => {
    nextPollAt.current = 0;
    pollFailures.current = 0;
    if (job) {
      stopPolling();
      timer.current = setInterval(() => void poll(job.jobId), 1500);
      await poll(job.jobId);
    } else {
      try {
        const { job: active } = await apiClient<{ job: SetupJobView | null }>("/console-bff/setup-jobs/current");
        setJob(active); setError(null);
        if (active && !TERMINAL_JOB_STATUSES.includes(active.status)) {
          stopPolling();
          timer.current = setInterval(() => void poll(active.jobId), 1500);
        }
      } catch { setError("進行中のセットアップを確認できませんでした。"); }
    }
  }, [job, poll, stopPolling]);

  return { job, issuedToken, error, errorCode, start, cancel, refresh };
}

export function useProbeKintoneAppFields() {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const mutate = useCallback(
    async (input: {
      kintoneAppId: string;
      guestSpaceId?: string | null;
      kintoneApiToken: string;
    }): Promise<KintoneAppProbeFieldsResponse> => {
      setIsLoading(true);
      setError(null);
      try {
        return await apiClient<KintoneAppProbeFieldsResponse>("/kintone-apps/probe-fields", {
          method: "POST",
          body: JSON.stringify(input),
        });
      } catch (err) {
        const message =
          isApiErrorLike(err)
            ? messageForProbeFieldsError(err)
            : err instanceof Error
              ? err.message
              : "Unknown error";
        setError(message);
        throw err;
      } finally {
        setIsLoading(false);
      }
    },
    [],
  );

  const reset = useCallback(() => setError(null), []);

  return { mutate, isLoading, error, reset };
}
